import { ChartCard, type ChartCardState } from "../../ChartCard";
import { ChannelActivityChart } from "../../ChannelActivityChart";
import type { TenantHealthTimeline } from "../../../../hooks/useAdminStatus";
import { activeBucketCount, hasAnyActivity } from "../../../../lib/chartBuckets";
import { rangeToWindowHours, type InstanceRange } from "../../../../lib/instanceDetailRange";

type ChannelActivityPanelProps = {
  readonly range: InstanceRange;
  readonly status: "loading" | "ready" | "error";
  readonly timeline: TenantHealthTimeline | null;
  readonly error: string | null;
};

const HEALTH_BUCKETS = 48;

const cardState = (
  status: ChannelActivityPanelProps["status"],
  timeline: TenantHealthTimeline | null,
): ChartCardState => {
  if (status === "loading") return "loading";
  if (status === "error") return "error";
  if (timeline == null || !hasAnyActivity(timeline.buckets)) return "empty";
  return "ready";
};

export const ChannelActivityPanel = ({
  range,
  status,
  timeline,
  error,
}: ChannelActivityPanelProps): JSX.Element => {
  // Same window derivation as HealthPanel — the per-tenant endpoint doesn't
  // echo bucketSeconds (windowHours * 3600 / 48).
  const bucketSeconds = (rangeToWindowHours(range) * 3600) / HEALTH_BUCKETS;
  const active = timeline != null ? activeBucketCount(timeline.buckets) : 0;
  return (
    <ChartCard
      title="Channel activity"
      subtitle={`${active} active windows`}
      state={cardState(status, timeline)}
      emptyText="No channel activity in this window."
      errorText={error ?? undefined}
    >
      {timeline != null && (
        <ChannelActivityChart buckets={timeline.buckets} bucketSeconds={bucketSeconds} />
      )}
    </ChartCard>
  );
};
